import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import ActressCard from "@/components/feature/ActressCard";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/base/EmptyState";
import useSEO from "@/hooks/useSEO";
import {
  getActressNames,
  getActressesOfWork,
  getRelatedWorks,
  getWorkById,
} from "@/utils/data";
import { newsList } from "@/mocks/news";

export default function WorkDetailPage() {
  const { id } = useParams<{ id: string }>();
  const work = id ? getWorkById(id) : undefined;

  const actresses = work ? getActressesOfWork(work) : [];
  const relatedWorks = work ? getRelatedWorks(work, 4) : [];
  const relatedNews = work
    ? newsList.filter((item) => item.relatedWorkIds.includes(work.id)).slice(0, 3)
    : [];

  useSEO({
    title: work
      ? `${work.title} - 作品介绍、主演女优与剧情简介 - 日本女优大全`
      : "未找到该作品 - 日本女优大全",
    description: work
      ? `${work.title}（${work.titleJp}），${work.year} 年${work.type}，导演${work.director}，主演${getActressNames(work).join("、")}。查看作品信息、剧情简介与相关推荐。`
      : "该作品条目不存在或已调整，请返回影视作品列表浏览其他作品。",
    keywords: work
      ? `${work.title},${work.type},${getActressNames(work).join(",")},日本女优作品`
      : "日本女优作品,影视作品列表",
  });

  if (!work) {
    return (
      <SiteLayout>
        <Breadcrumbs items={[{ label: "影视作品", href: "/work-list" }, { label: "未找到作品" }]} />
        <EmptyState
          icon="ri-film-line"
          title="未找到该作品信息"
          description="你访问的作品条目不存在，可能链接有误或资料已调整。可以返回作品列表浏览全部条目，或从女优详情页进入相关作品。"
          actionLabel="返回影视作品列表"
          actionHref="/work-list"
        />
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "影视作品", href: "/work-list" }, { label: work.title }]} />

      <section className="grid gap-6 md:grid-cols-[200px_1fr] lg:grid-cols-[240px_1fr] lg:gap-8">
        <div className="overflow-hidden rounded-lg border border-background-200 bg-background-200">
          <div className="aspect-[2/3] w-full">
            <img
              src={work.cover}
              alt={`${work.title} 影视作品封面`}
              title={`${work.title} 日本女优影视作品介绍`}
              className="h-full w-full object-cover object-top"
            />
          </div>
        </div>

        <div>
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-primary-500 px-2.5 py-1 font-label text-[11px] text-background-50">
              {work.type}
            </span>
            <span className="rounded-md bg-secondary-100 px-2.5 py-1 font-label text-[11px] text-secondary-900">
              {work.year} 年
            </span>
            {work.genres.map((genre) => (
              <span
                key={genre}
                className="rounded-md border border-background-300 px-2.5 py-1 font-label text-[11px] text-foreground-600"
              >
                {genre}
              </span>
            ))}
          </div>

          <h1 className="mt-3 font-heading text-[28px] font-black leading-tight text-foreground-950 md:text-[36px]">
            {work.title}
          </h1>
          <p className="mt-1 font-label text-sm text-foreground-500">{work.titleJp}</p>

          <div className="mt-4 flex flex-wrap items-center gap-5">
            <p className="flex items-baseline gap-1.5">
              <span className="font-heading text-2xl font-black text-primary-700">{work.rating.toFixed(1)}</span>
              <span className="font-label text-[11px] text-foreground-500">/ 10 评分</span>
            </p>
            <p className="flex items-center gap-1.5 font-label text-xs text-foreground-600">
              <span className="flex h-4 w-4 items-center justify-center text-accent-600">
                <i className="ri-eye-line" aria-hidden="true"></i>
              </span>
              {work.views.toLocaleString()} 次浏览
            </p>
          </div>

          <dl className="mt-5 grid gap-3 sm:grid-cols-2">
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">上映日期</dt>
              <dd className="mt-0.5 font-label text-sm text-foreground-900">{work.releaseDate}</dd>
            </div>
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5">
              <dt className="font-label text-[10px] text-foreground-500">导演</dt>
              <dd className="mt-0.5 font-label text-sm text-foreground-900">{work.director}</dd>
            </div>
            <div className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5 sm:col-span-2">
              <dt className="font-label text-[10px] text-foreground-500">主演女优</dt>
              <dd className="mt-1 flex flex-wrap gap-2">
                {actresses.map((actress) => (
                  <Link
                    key={actress.id}
                    to={`/detail/actress/${actress.id}`}
                    className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 font-label text-xs text-primary-800 transition-colors hover:bg-primary-100"
                  >
                    <i className="ri-user-line" aria-hidden="true"></i>
                    {actress.name}
                  </Link>
                ))}
              </dd>
            </div>
          </dl>

          <p className="clamp-3 mt-4 max-w-2xl text-sm leading-relaxed text-foreground-700">
            {work.synopsis}
          </p>

          <div className="mt-5 flex flex-wrap items-center gap-3">
            <Link
              to="/work-list"
              className="inline-flex items-center gap-1.5 rounded-md border border-background-300 px-4 py-2 font-label text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700"
            >
              <i className="ri-arrow-left-line" aria-hidden="true"></i>
              返回作品列表
            </Link>
            {actresses.length > 0 ? (
              <Link
                to={`/detail/actress/${actresses[0].id}`}
                className="inline-flex items-center gap-1.5 rounded-md bg-primary-500 px-4 py-2 font-label text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                查看主演女优档案
                <i className="ri-arrow-right-line" aria-hidden="true"></i>
              </Link>
            ) : null}
          </div>
        </div>
      </section>

      <div className="mt-10 grid gap-8 lg:grid-cols-[1fr_300px]">
        <div>
          <section>
            <h2 className="border-b border-background-200 pb-3 font-heading text-xl font-bold text-foreground-950">
              剧情简介
            </h2>
            <div className="mt-4 rounded-lg border border-background-200 bg-background-50 p-5">
              <p className="text-[15px] leading-8 text-foreground-700">{work.synopsis}</p>
              <p className="mt-3 text-[15px] leading-8 text-foreground-700">
                本条目为站内资料索引，收录作品名称、上映信息、主创阵容与简介，不提供任何播放或下载入口。若需要进一步了解该作品，可前往女优档案查看其同期作品与相关报道。
              </p>
            </div>
          </section>

          {actresses.length > 0 ? (
            <section className="mt-10">
              <div className="mb-4 flex items-end justify-between gap-3 border-b border-background-200 pb-3">
                <h2 className="font-heading text-xl font-bold text-foreground-950">关联女优</h2>
                <Link to="/category" className="font-label text-xs text-primary-700 hover:text-primary-600">
                  全部分类
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 xl:grid-cols-3">
                {actresses.map((actress) => (
                  <ActressCard key={actress.id} actress={actress} />
                ))}
              </div>
            </section>
          ) : null}

          {relatedWorks.length > 0 ? (
            <section className="mt-10">
              <div className="mb-4 flex items-end justify-between gap-3 border-b border-background-200 pb-3">
                <h2 className="font-heading text-xl font-bold text-foreground-950">
                  相关推荐
                  <span className="ml-2 font-label text-xs font-normal text-foreground-500">
                    基于题材标签匹配
                  </span>
                </h2>
                <Link to="/work-list" className="font-label text-xs text-primary-700 hover:text-primary-600">
                  更多作品
                </Link>
              </div>
              <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                {relatedWorks.map((item) => (
                  <WorkCard key={item.id} work={item} actressNames={getActressNames(item)} />
                ))}
              </div>
            </section>
          ) : null}
        </div>

        <aside className="space-y-4">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5">
            <h2 className="font-heading text-base font-bold text-foreground-950">作品信息</h2>
            <dl className="mt-3 space-y-2.5">
              {[
                { label: "作品名称", value: work.title },
                { label: "日文原名", value: work.titleJp },
                { label: "类型", value: work.type },
                { label: "上映日期", value: work.releaseDate },
                { label: "导演", value: work.director },
                { label: "评分", value: `${work.rating.toFixed(1)} / 10` },
                { label: "站内浏览", value: `${work.views.toLocaleString()} 次` },
              ].map((row) => (
                <div
                  key={row.label}
                  className="flex items-start justify-between gap-3 border-b border-background-100 pb-2 last:border-b-0 last:pb-0"
                >
                  <dt className="shrink-0 font-label text-[11px] text-foreground-500">{row.label}</dt>
                  <dd className="text-right font-label text-xs text-foreground-900">{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {relatedNews.length > 0 ? (
            <div className="rounded-lg border border-background-200 bg-background-50 p-5">
              <h2 className="font-heading text-base font-bold text-foreground-950">相关新闻</h2>
              <ul className="mt-3 space-y-3">
                {relatedNews.map((item) => (
                  <li key={item.id}>
                    <Link to={`/detail/news/${item.id}`} className="group block">
                      <span className="block font-label text-[11px] text-foreground-400">{item.date}</span>
                      <span className="mt-0.5 block text-sm leading-snug text-foreground-800 transition-colors group-hover:text-primary-700">
                        {item.title}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="rounded-lg border border-accent-200 bg-accent-50 p-5">
            <h2 className="font-heading text-base font-bold text-accent-950">资料说明</h2>
            <p className="mt-2 font-label text-xs leading-relaxed text-accent-900/80">
              作品条目中的评分与浏览量为站内示例数据，用于演示排序与推荐逻辑。本站不提供视频播放、下载或任何交易入口。
            </p>
          </div>
        </aside>
      </div>
    </SiteLayout>
  );
}